<template>
  <div class="zen-changes" v-if="isLoading || imports.length > 0">
    <div class="d-input-group">
      <label class="d-label d-label--white">{{ $t('vue2.zen_import.changes_from_import') }}</label>
      <p class="zen-changes__help">{{ $t('vue2.zen_import.changes_from_import_help') }}</p>

      <div class="box" v-if="isLoading">
        <Loader bgColor="#fff"></Loader>
      </div>

      <div class="zen-imports" v-else>
        <div class="zen-import-row"
             v-for="imp in imports"
             :key="imp.id"
             @click="openImport(imp)">
          <div class="zen-import-row__left">
            <p class="zen-import-row__title">{{ imp.title || $t('vue2.zen_import.import_from_zen') }}</p>
            <p class="zen-import-row__meta">
              <span>{{ showHumanizeDate(imp.created_at) }}</span>
              <span v-if="imp.summary" class="zen-import-row__counts">
                · {{ calendarChangeCount(imp) }} {{ $t('vue2.zen_import.calendars_label') }},
                {{ subjectChangeCount(imp) }} {{ $t('vue2.zen_import.subjects_label') }}
              </span>
              <span v-else class="zen-import-row__processing">· {{ $t('vue2.zen_import.import_processing') }}</span>
            </p>
          </div>
          <button class="bttn zen-view-bttn" @click.stop="openImport(imp)">
            {{ $t('vue2.zen_import.view_changes') }}
          </button>
        </div>
      </div>
    </div>

    <DModal v-if="activeImport" :show-close="false" :is-bg-overlay="true" @close="closeImport">
      <div class="zen-changes-modal" slot="insertComponent">
        <div class="do-modal__header">
          <h2>{{ activeImport.title || $t('vue2.zen_import.changes_from_import') }}</h2>
          <div class="icon__bg-circle" @click="closeImport">
            <span class="icon-cross2"></span>
          </div>
        </div>

        <div class="do-modal__body">
          <p v-if="isEmptyImport" class="text-explainer">
            {{ $t('vue2.zen_import.no_changes_in_import') }}
          </p>

          <template v-else>
            <section v-for="group in classGroups" :key="group.calendarId" class="zen-changes-group">
              <label class="d-label d-label__divider">{{ group.name }}</label>

              <table v-if="group.createdItems.length" class="do-table do-table--striped zen-table">
                <thead>
                  <tr>
                    <th class="zen-table__type">{{ $t('vue2.zen_import.type_column') }}</th>
                    <th class="zen-table__id">ID</th>
                    <th>{{ $t('vue2.zen_import.field_name') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="zen-table__section">
                    <td colspan="3">{{ $t('vue2.zen_import.created_title') }}</td>
                  </tr>
                  <tr v-for="item in group.createdItems" :key="'c-' + item.key">
                    <td class="zen-table__type">{{ item.type }}</td>
                    <td class="zen-table__id">{{ item.id }}</td>
                    <td>{{ item.name }}</td>
                  </tr>
                </tbody>
              </table>

              <table v-if="group.changedItems.length" class="do-table do-table--striped zen-table">
                <thead>
                  <tr>
                    <th class="zen-table__type">{{ $t('vue2.zen_import.type_column') }}</th>
                    <th class="zen-table__id">ID</th>
                    <th>{{ $t('vue2.zen_import.field_name') }}</th>
                    <th>{{ $t('vue2.zen_import.excel_field') }}</th>
                    <th>{{ $t('vue2.zen_import.excel_old_value') }}</th>
                    <th>{{ $t('vue2.zen_import.excel_new_value') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="zen-table__section">
                    <td colspan="6">{{ $t('vue2.zen_import.changed_title') }}</td>
                  </tr>
                  <tr v-for="item in group.changedItems" :key="'m-' + item.key">
                    <td class="zen-table__type">{{ item.type }}</td>
                    <td class="zen-table__id">{{ item.id }}</td>
                    <td>{{ item.name }}</td>
                    <td>
                      <div v-for="diff in diffList(item.changes)" :key="diff.field">{{ diff.label }}</div>
                    </td>
                    <td>
                      <div class="zen-diff__from" v-for="diff in diffList(item.changes)" :key="diff.field">{{ diff.from }}</div>
                    </td>
                    <td>
                      <div class="zen-diff__to" v-for="diff in diffList(item.changes)" :key="diff.field">{{ diff.to }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
          </template>
        </div>

        <div class="do-modal__footer zen-changes-modal__footer">
          <button class="do-bttn do-bttn--with-icon" v-if="!isEmptyImport" @click="asyncLoadXLSXAndDownload">
            <span v-if="isDownloadingExcel" class="do-spinner do-spinner--small"></span>
            <span v-else class="icon-download"></span>
            {{ $t('vue2.zen_import.download_to_excel') }}
          </button>
          <button class="do-bttn" @click="closeImport">{{ $t('vue2.common.done') }}</button>
        </div>
      </div>
    </DModal>
  </div>
</template>

<script>
import moment from 'moment'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import Loader from '../common/Loader'
import DModal from '../common/DModal'
import ZenChangeAction from './ZenChangeAction'

export default {
  components: {Loader, DModal, ZenChangeAction},
  data () {
    return {
      isLoading: false,
      imports: [],
      activeImport: null,
      itemState: {},
      schoolCalendars: [],
      isDownloadingExcel: false
    }
  },
  computed: {
    schoolId () {
      return window.SCHOOL.id
    },
    summary () {
      return this.activeImport && this.activeImport.summary ? this.activeImport.summary : {}
    },
    createdCalendars () {
      return this.summary.created_calendars || []
    },
    changedCalendars () {
      return this.summary.changed_calendars || []
    },
    createdSubjects () {
      return this.summary.created_subjects || []
    },
    changedSubjects () {
      return this.summary.changed_subjects || []
    },
    calendarNamesById () {
      const map = {}
      this.schoolCalendars.forEach(cal => {
        map[cal.id] = cal.name
      })
      this.createdCalendars.concat(this.changedCalendars).forEach(cal => {
        map[cal.id] = cal.name
      })
      return map
    },
    classGroups () {
      const groups = {}
      const ensure = (calendarId, name) => {
        if (!groups[calendarId]) {
          groups[calendarId] = {
            calendarId,
            name: name || this.calendarName(calendarId),
            createdItems: [],
            changedItems: []
          }
        } else if (name) {
          groups[calendarId].name = name
        }
        return groups[calendarId]
      }
      const calendarType = this.$t('vue2.zen_import.type_calendar')
      const subjectType = this.$t('vue2.zen_import.type_subject')
      this.createdCalendars.forEach(cal => {
        ensure(cal.id, cal.name).createdItems.push({
          key: 'cal-' + cal.id, type: calendarType, id: cal.id, name: cal.name
        })
      })
      this.changedCalendars.forEach(cal => {
        ensure(cal.id, cal.name).changedItems.push({
          key: 'cal-' + cal.id, type: calendarType, id: cal.id, name: cal.name, changes: cal.changes
        })
      })
      this.createdSubjects.forEach(sub => {
        ensure(sub.calendar_id).createdItems.push({
          key: 'sub-' + sub.id, type: subjectType, id: sub.id, name: sub.name
        })
      })
      this.changedSubjects.forEach(sub => {
        ensure(sub.calendar_id).changedItems.push({
          key: 'sub-' + sub.id, type: subjectType, id: sub.id, name: sub.name, changes: sub.changes
        })
      })
      return Object.keys(groups)
        .map(id => groups[id])
        .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, {numeric: true}))
    },
    isEmptyImport () {
      return this.createdCalendars.length < 1 &&
        this.changedCalendars.length < 1 &&
        this.createdSubjects.length < 1 &&
        this.changedSubjects.length < 1
    }
  },
  methods: {
    readImports () {
      this.isLoading = true
      docendo.listZenImports(this.schoolId, imports => {
        this.imports = Array.isArray(imports) ? imports : []
        this.isLoading = false
        this.openImportFromUrl()
      }, failure => {
        console.log('failure', failure)
        this.isLoading = false
      })
    },
    readSchoolCalendars () {
      docendo.calendarsForSchool(this.schoolId, calendars => {
        this.schoolCalendars = Array.isArray(calendars) ? calendars : []
      }, failure => {
        console.log('failure', failure)
      })
    },
    calendarName (calendarId) {
      return this.calendarNamesById[calendarId] || ('#' + calendarId)
    },
    openImportFromUrl () {
      const match = window.location.search.match(/[?&]id=(\d+)/)
      if (!match) {
        return
      }
      const id = match[1]
      const imp = this.imports.find(item => String(item.id) === id)
      if (imp) {
        this.openImport(imp)
      }
    },
    openImport (imp) {
      this.itemState = {}
      this.activeImport = imp
    },
    closeImport () {
      this.activeImport = null
    },
    stateKey (type, id) {
      return type + '-' + id
    },
    setItemState (key, state) {
      this.$set(this.itemState, key, state)
    },
    calendarChangeCount (imp) {
      const s = imp.summary || {}
      return (s.created_calendars || []).length + (s.changed_calendars || []).length
    },
    subjectChangeCount (imp) {
      const s = imp.summary || {}
      return (s.created_subjects || []).length + (s.changed_subjects || []).length
    },
    fieldLabel (field) {
      const key = 'vue2.zen_import.field_' + field
      const translated = this.$t(key)
      return translated === key ? field : translated
    },
    diffList (changes) {
      if (!changes) {
        return []
      }
      return Object.keys(changes).map(field => ({
        field,
        label: this.fieldLabel(field),
        from: this.formatValue(changes[field][0]),
        to: this.formatValue(changes[field][1])
      }))
    },
    formatValue (value) {
      if (value === null || value === undefined || value === '') {
        return '–'
      }
      return value
    },
    oldValues (changes) {
      const result = {}
      Object.keys(changes || {}).forEach(field => {
        result[field] = changes[field][0]
      })
      return result
    },
    revertCalendar (item) {
      const key = this.stateKey('cal', item.id)
      this.setItemState(key, 'loading')
      const revertTo = this.revertableChanges(item.changes, ['name'])
      docendo.schoolCalendarsUpdate(this.schoolId, item.id, revertTo, () => {
        this.setItemState(key, 'reverted')
      }, failure => {
        this.handleFailure(key, failure)
      })
    },
    revertSubject (item) {
      const key = this.stateKey('sub', item.id)
      this.setItemState(key, 'loading')
      const revertTo = this.revertableChanges(item.changes, ['name', 'color'])
      docendo.bulkUpdateSubjects(this.schoolId, [item.id], revertTo, () => {
        this.setItemState(key, 'reverted')
      }, failure => {
        this.handleFailure(key, failure)
      })
    },
    revertableChanges (changes, allowedFields) {
      const old = this.oldValues(changes)
      const result = {}
      allowedFields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(old, field)) {
          result[field] = old[field]
        }
      })
      return result
    },
    asyncLoadXLSXAndDownload () {
      this.isDownloadingExcel = true
      if (window.XLSX) {
        this.downloadExcel()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        const intervalTimer = setInterval(() => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            this.downloadExcel()
          }
        }, 500)
      }
    },
    downloadExcel () {
      const XLSX = window.XLSX
      const typeHeader = this.$t('vue2.zen_import.type_column')
      const nameHeader = this.$t('vue2.zen_import.field_name')
      const idHeader = 'ID'
      const fieldHeader = this.$t('vue2.zen_import.excel_field')
      const fromHeader = this.$t('vue2.zen_import.excel_old_value')
      const toHeader = this.$t('vue2.zen_import.excel_new_value')
      const rows = []

      this.classGroups.forEach(group => {
        rows.push([group.name])

        if (group.createdItems.length) {
          rows.push([this.$t('vue2.zen_import.created_title')])
          rows.push([typeHeader, idHeader, nameHeader])
          group.createdItems.forEach(item => {
            rows.push([item.type, item.id, item.name])
          })
        }

        if (group.changedItems.length) {
          rows.push([this.$t('vue2.zen_import.changed_title')])
          rows.push([typeHeader, idHeader, nameHeader, fieldHeader, fromHeader, toHeader])
          group.changedItems.forEach(item => {
            this.diffList(item.changes).forEach(diff => {
              rows.push([item.type, item.id, item.name, diff.label, diff.from, diff.to])
            })
          })
        }

        rows.push([])
      })

      const ws = XLSX.utils.aoa_to_sheet(rows)
      ws['!cols'] = [{wch: 12}, {wch: 10}, {wch: 30}, {wch: 15}, {wch: 25}, {wch: 25}]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'ZEN import')
      XLSX.writeFile(wb, 'docendo_zen_import_' + this.activeImport.id + '.xlsx')
      this.isDownloadingExcel = false
    },
    handleFailure (key, failure) {
      console.log('failure', failure)
      this.setItemState(key, 'idle')
      eventbus.toast = {text: this.$t('vue2.common.something_went_wrong'), success: false}
    },
    showHumanizeDate (date) {
      moment.locale(window.USER.language)
      const diff = moment().diff(moment(date))
      return moment.duration(diff).humanize() + ' ' + this.$t('vue2.common.ago')
    }
  },
  mounted () {
    this.readImports()
    this.readSchoolCalendars()
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.zen-changes {
  margin-top: 2rem;
}

.zen-changes__help {
  color: #ffffff;
  opacity: 0.85;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.zen-imports {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
}

.zen-import-row {
  background: #ffffff;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ececf0;
  box-shadow: 0 2px 5px -1px rgb(50 50 93 / 25%), 0 1px 3px -1px rgb(0 0 0 / 30%);
  transition: 0.25s;
}

.zen-import-row:hover {
  background: #f0f5ff;
}

.zen-import-row__title {
  font-weight: bold;
  margin: 0;
}

.zen-import-row__meta {
  margin: 0.15rem 0 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.zen-import-row__processing {
  font-style: italic;
}

.zen-changes-modal {
  min-width: 600px;
  max-width: calc(100vw - 2rem);
  width: 80vw;
  height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
}

.zen-changes-modal .do-modal__header,
.zen-changes-modal .do-modal__footer {
  flex-shrink: 0;
}

.zen-changes-modal .do-modal__body {
  flex: 1;
  max-height: none;
  overflow-y: auto;
}

.zen-changes-group {
  margin-bottom: 1rem;
}

.zen-changes-modal__footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.zen-table {
  font-size: 0.85rem;
}

.zen-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.zen-table td {
  vertical-align: top;
  color: var(--dark-blue);
}

.zen-table th.zen-table__type,
.zen-table td.zen-table__type {
  width: 90px;
}

.zen-table th.zen-table__id,
.zen-table td.zen-table__id {
  width: 70px;
}

.zen-table td.zen-table__id {
  color: #6b7280;
}

.zen-table th.zen-table__action,
.zen-table td.zen-table__action {
  width: 140px;
  text-align: right;
}

.do-table.do-table--striped tbody tr.zen-table__section,
.do-table.do-table--striped tbody tr.zen-table__section:hover {
  background: #eef2f7;
}

.zen-table tbody tr.zen-table__section td {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  color: var(--dark-blue);
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  border-left: none;
}

.zen-diff__from {
  text-decoration: line-through;
  color: #6b7280;
}

.zen-diff__to {
  font-weight: 600;
  color: var(--dark-blue);
}

.zen-view-bttn {
  height: 28px;
  padding: 0 0.75rem;
  font-size: 0.85rem;
  flex-shrink: 0;
  white-space: nowrap;
}

.d-label__divider {
  border: 1px solid #d8d8d8;
  background: #efefef;
  padding: 0.25rem 1rem;
  border-width: 1px 0;
  display: block;
  margin-bottom: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// ZenImportChanges.vue?69f160b0